import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { roles } from '../../utils/roles';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { abortController } from '../../utils/abortController';
import { BiReset } from 'react-icons/bi';
import withAuth from '../../lib/withAuth';
import useUser from '../../hooks/useUser';
import useSessionExpire from '../../hooks/useSessionExpire';
import usePageFilterPersistence from '../../hooks/usePageFilterPersistence';
import Button from '@mui/material/Button';
import Backdrop from '@mui/material/Backdrop';
import Grid from '@mui/material/Grid';
import ButtonGroup from '@mui/material/ButtonGroup';
import Loader from '../../components/Loader';
import Alert from '../../components/Alert';
import dayjs from 'dayjs';
import axios from 'axios';
import { Modal } from 'react-bootstrap';
import MaterialReactDataTable from '../../components/MaterialReactDataTable';
import { getPermissions } from '../../lib/accessControl';

const PeriodLockRequests = ({ logOut = () => {} }) => 
{
  const [isLoading, setIsLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const location = useLocation();
  const { pathname } = useLocation();

  /* SESSION EXPIRY VARIABLES */
  const triggerSessionExpire = useSessionExpire();

  /* AUTHENTICATION VARIABLES */
  const currentUser = useUser();

  /* AUTHORIZATION VARAIBLES */
  const { isWritable = false } = getPermissions(currentUser?.role, pathname);
  const isFinanceRole = currentUser?.role === roles[8];

  /* PERSISTED FILTERS (redux, keyed by route) */
  const pageKey = `${pathname}::PeriodLockRequests`;
  const {
    filterValues: persistedFilters,
    setFilter: persistFilter,
    setFilters: persistFilters
  } = usePageFilterPersistence(pageKey);
  const hasHydratedFiltersRef = useRef(false);
  const globalFilterDebounceRef = useRef(null);

  /* DATE VARIABLES */
  const [startDate, setStartDate] = useState(dayjs().startOf("month").format("MM/DD/YYYY"));
  const [endDate, setEndDate] = useState(dayjs().format("MM/DD/YYYY"));

  /* CONDITIONAL FILTER VARIABLES */
  const [isFilterable, setIsFilterable] = useState(true);
  const [filter, setFilter] = useState(true);
  const [isFiltered, setIsFiltered] = useState(true);
  
  /* DATATABLE VARIABLES */
  const [periodLockRequestsData, setPeriodLockRequestsData] = useState([]);
  const [isDataChanged, setIsDataChanged] = useState(false);
  const [toggleDisplayData, setToggleDisplayData] = useState(false);
  const tableRef = useRef();

    /* DATATABLE ROW VARIABLES */
  const [rowData, setRowData] = useState({});

  /* DATATABLE GLOBAL FILTER VARIABLES */
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  
  /* MODAL VARIABLES */
  const [showLockStatusModal, setShowLockStatusModal] = useState(false);

  useEffect(() => 
  {
    if (startDate || endDate)
    {
      setIsFilterable(true);
    }
    else
    {
      setIsFilterable(false);
    }

  }, [startDate, endDate]);

  useEffect(() => 
  {
    const fetchData = async () => 
    {
      setIsLoading(true);
      setShowAlert(false);
      setAlertMessage("");
      setAlertType("");

      await axios({
        method: "post",
        url: "/api/GetAllPeriodLockRequests",
        data: {
          start_date: startDate,
          end_date: endDate,
          filter_value: globalFilterValue
        },
        signal: abortController.signal
      })
      .then((response) => 
      {
        setIsLoading(false);
        const { status, data } = response;

        if (status === 200) 
        {
          const { period_lock_data: periodLockRequestsData = []} = data || {};

          if (filter && periodLockRequestsData.length === 0)
          {
            setAlertMessage("No data found that matches the selected filter options.");
            setAlertType("info");
            setShowAlert(true);
          }

          setPeriodLockRequestsData(periodLockRequestsData);
        }
        else 
        {
          setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
          setAlertType("error");
          setShowAlert(true);
        }
      })
      .catch((error) => 
      {
        console.error("Get All PeriodLockRequests Data Api: ", error);
        setIsLoading(false);

        if (axios.isCancel(error) || error.code === "ERR_CANCELED") 
        {
          return;
        }

        const status = error?.response?.status;

        if (status === 403) 
        {
          triggerSessionExpire();
        }
        else 
        {
          setAlertMessage(
            status === 401
              ? "Unauthorized access. You do not have the required permissions to perform this action."
              : status === 429
              ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
              : "An error occurred while processing your request. Please try again later or contact the site administrator."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) 
          {
            setTimeout(logOut, 3000);
          }
        }
      });
    }

    fetchData();

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [filter, toggleDisplayData]);

  /* -------- PERSISTED FILTERS: hydrate on mount, and force a single
     auto re-fetch if we land on the page with any persisted filter value
     already set (start date, end date, or global search) - those aren't
     dependencies of the fetch effect above, so we nudge it via
     toggleDisplayData. Hydration and this decision are read straight from
     persistedFilters (not component state) to avoid a race with state
     updates not yet being reflected in a re-render. -------- */
  useEffect(() => {
    if (hasHydratedFiltersRef.current) return;
    hasHydratedFiltersRef.current = true;

    if (!persistedFilters) return;

    const {
      startDate: persistedStartDate,
      endDate: persistedEndDate,
      globalFilterValue: persistedGlobalFilterValue
    } = persistedFilters;

    if (persistedStartDate) setStartDate(persistedStartDate);
    if (persistedEndDate) setEndDate(persistedEndDate);
    if (persistedGlobalFilterValue) setGlobalFilterValue(persistedGlobalFilterValue);

    const hadPersistedFilter =
      (persistedStartDate && persistedStartDate !== startDate) ||
      (persistedEndDate && persistedEndDate !== endDate) ||
      persistedGlobalFilterValue;

    if (hadPersistedFilter) {
      setIsFiltered(true);
      setFilter(true);
      setToggleDisplayData((previous) => !previous);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  /* -------- PERSISTED FILTERS: write date range back to redux immediately -------- */
  useEffect(() => {
    if (!hasHydratedFiltersRef.current) return;

    persistFilters({
      startDate,
      endDate
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [startDate, endDate]);

  /* -------- PERSISTED FILTERS: write global search value (debounced) -------- */
  useEffect(() => {
    if (!hasHydratedFiltersRef.current) return;

    if (globalFilterDebounceRef.current) clearTimeout(globalFilterDebounceRef.current);

    globalFilterDebounceRef.current = setTimeout(() => {
      persistFilter('globalFilterValue', globalFilterValue || "");
    }, 400);

    return () => clearTimeout(globalFilterDebounceRef.current);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [globalFilterValue]);

  const convertToDayJsObject = (date) => 
  {
    const convertedDate = date ? dayjs(date, "MM/DD/YYYY") : null;
    return convertedDate;
  }

  const handleDateChange = (newDate, type) => 
  {
    const date = newDate ? dayjs(newDate).format("MM/DD/YYYY") : newDate;
    type === "Start Date" ? setStartDate(date) :  setEndDate(date);
  }

  const handleFilter = () => 
  {
      setIsFiltered(true);
      setFilter(true);
      setToggleDisplayData(!toggleDisplayData);
  }

  const handleReset = () => 
  {
    setStartDate(null);
    setEndDate(null);
    setIsFilterable(false);
    setFilter(false);
    setIsFiltered(false);
    setToggleDisplayData(!toggleDisplayData);
  }

  const handleGlobalFilter = async (rowData) => 
  {

    setIsLoading(true);
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");

    await axios({
      method: "post",
      url: "/api/FilterTableData",
      data: {
        pathname: location?.pathname,
        table_data: rowData,
        filter_value: globalFilterValue
      },
      signal: abortController.signal
    })
    .then((response) => 
    {
      setIsLoading(false);
      const { status, data } = response;

      if (status === 200) 
      {
        tableRef?.current?.setFilteredData(data || []);
      }
      else 
      {
        setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
        setAlertType("error");
        setShowAlert(true);
      }
    })
    .catch((error) => 
    {
      console.log("Filter Table Data Api: ", error);
      setIsLoading(false);

      if (axios.isCancel(error) || error.code === "ERR_CANCELED") 
      {
        return;
      }

      const status = error?.response?.status;

      if (status === 403) 
      {
        triggerSessionExpire();
      }
      else 
      {
        setAlertMessage(
          status === 401
            ? "Unauthorized access. You do not have the required permissions to perform this action."
            : status === 429
            ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
            : "An error occurred while processing your request. Please try again later or contact the site administrator."
        );
        setAlertType("error");
        setShowAlert(true);

        if (status === 429) 
        {
          setTimeout(logOut, 3000);
        }
      }
    });
  }

    const handleLockStatusWrapper = (rowData) => 
      {
        setRowData(rowData);
        setShowLockStatusModal(true);
      }
     const handleLockStatus = useCallback(async () => 
    {
      setIsLoading(true);
      setShowAlert(false);
      setAlertMessage("");
      setAlertType("");
  
      await axios({
        method: "post",
        url: "/api/SetPeriodLockStatus",
        data: { 
          user_id: currentUser?.id,
          status: rowData?.Status,
          period_lock_request_id: rowData?.id,
        },
        signal: abortController.signal
      })
      .then((response) => 
      {
        setIsLoading(false);
        const { status, data } = response;
  
        if (status === 200) 
        {
          setShowLockStatusModal(false);
          setAlertMessage(data?.message)
          setAlertType("error");
          setShowAlert(true);
          setToggleDisplayData(!toggleDisplayData);
        }
        else
        {
          setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
          setAlertType("error");
          setShowAlert(true);
        }
      })
      .catch((error) => 
      { 
        console.log("Set Bank Status Api: ", error);
        setIsLoading(false);
  
        if (axios.isCancel(error) || error.code === "ERR_CANCELED") 
        {
          return;
        }
        
        const status = error?.response?.status;
  
        if (status === 403) 
        {
          triggerSessionExpire();
        } 
        else 
        {
          setAlertMessage(
            status === 401
              ? "Unauthorized access. You do not have the required permissions to perform this action."
              : status === 429
              ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
              : "An error occurred while processing your request. Please try again later or contact the site administrator."
          );
          setAlertType("error");
          setShowAlert(true);
      
          if (status === 429) 
          {
            setTimeout(logOut, 3000);
          }
        }
      });
  
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [rowData?.Status, rowData?.id, toggleDisplayData]
    );

    const handleCloseModal = () => 
    {
      setShowLockStatusModal(false);
    }

  return (
    <div className = "content-container d-flex flex-column container mass-update position-relative">
        <Alert
            show = {showAlert}
            message = {alertMessage}
            type = {alertType}
            setShow = {setShowAlert}
        />

        <Backdrop
            sx = {{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open = {isLoading}
        >
            <Loader/>
        </Backdrop>

        <h4 className = "page-heading m-0">
            Requests
        </h4>
        
        <Grid container spacing = {1} justifyContent = "center" alignItems = "center">

            <Grid size = {{ xs: 2 }}>
                <LocalizationProvider dateAdapter = {AdapterDayjs}>
                    <DesktopDatePicker
                    inputFormat = "DD-MMM-YYYY"
                    label = "Start Date"
                    className = "date-picker w-100"
                    maxDate = {convertToDayJsObject(endDate)}
                    value = {convertToDayJsObject(startDate)}
                    onChange = {(newDate) => handleDateChange(newDate, "Start Date")}
                    onAccept = {(newDate) => handleDateChange(newDate, "Start Date")}
                    slotProps = {{
                        actionBar: {
                        actions: ["clear"]
                        },
                        textField: {
                        InputProps: {
                            size: "small",
                            disabled: true
                        }
                        }
                    }}
                    />
                </LocalizationProvider>
            </Grid>

            <Grid size = {{ xs: 2 }}>
                <LocalizationProvider dateAdapter = {AdapterDayjs}>
                    <DesktopDatePicker
                    inputFormat = "DD-MMM-YYYY"
                    label = "End Date"
                    className = "date-picker w-100"
                    minDate = {convertToDayJsObject(startDate)}
                    value = {convertToDayJsObject(endDate)}
                    onChange = {(newDate) => handleDateChange(newDate, "End Date")}
                    onAccept = {(newDate) => handleDateChange(newDate, "End Date")}
                    slotProps = {{
                        actionBar: {
                        actions: ["clear"]
                        },
                        textField: {
                        InputProps: {
                            size: "small",
                            disabled: true
                        }
                        }
                    }}
                    />
                </LocalizationProvider>
            </Grid>

            <Grid size = {{ xs: 2 }}>
                <ButtonGroup variant = "contained" disableElevation = {true}>
                    <Button type = "submit" className = "filter-button" color = "primary" disabled = {!isFilterable} onClick = {handleFilter}>Filter</Button>
                    <Button className = "reset-button" color = "info" disabled = {!isFiltered} startIcon = {<BiReset />} onClick = {handleReset}></Button>
                </ButtonGroup>
            </Grid>
        </Grid>

        <LocalizationProvider dateAdapter = {AdapterDayjs}>
            <MaterialReactDataTable
                ref = {tableRef}
                title = "Period Lock Requests"
                pageKey = {pageKey}
                isWritable = {isWritable}
                isLoading = {isLoading}
                isDataChanged = {isDataChanged}
                globalFilterValue = {globalFilterValue}
                tableData = {periodLockRequestsData}
                convertToDayJsObject = {convertToDayJsObject}
                setGlobalFilterValue = {setGlobalFilterValue}
                setIsDataChanged = {setIsDataChanged}
                handleGlobalFilter = {handleGlobalFilter}
                enableExpandAll = {false}
                showActions = {isFinanceRole}
                handleRecordStatusChange = {handleLockStatusWrapper}
                
            />
        </LocalizationProvider>

        <Modal show = {showLockStatusModal} onHide = {handleCloseModal} centered = {true}>
          <Modal.Header style={{padding: '7px 16px'}}closeButton = {true}>
            <Modal.Title>Are you sure?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <span>Do you really want to change the Lock status?</span>
          </Modal.Body>
          <Modal.Footer className = "gap-2">
            <Button variant = "outlined" size = "small" onClick = {handleCloseModal}>
              Cancel
            </Button>
            <Button
              variant = "contained"
              size = "small"
              onClick = {handleLockStatus}
            >
              Submit
            </Button>
          </Modal.Footer>
        </Modal>
    </div>
  );
}

export default withAuth(PeriodLockRequests)([
    roles[2], 
    roles[4], 
    roles[5], 
    roles[8],
    roles[10], 
    roles[11], 
    roles[13], 
    roles[14],
    roles[16], 
    roles[17]
]);